import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, FileCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import SalesPageHeader from '../components/sales/SalesPageHeader';
import { useSalesEmbedded } from '../hooks/useSalesEmbedded';
import PortalBackground from '../components/PortalBackground';

const VALID_TABS = new Set(['proposal', 'contracts', 'buildProposal', 'buildContract']);

const PortalSalesProposals = ({ embedded: embeddedProp }) => {
  const embedded = useSalesEmbedded(embeddedProp);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(() =>
    VALID_TABS.has(tabParam) ? tabParam : 'proposal'
  );
  const [currentPage, setCurrentPage] = useState(1);

  const selectTab = useCallback(
    (tab) => {
      setActiveTab(tab);
      setCurrentPage(1);
      const next = new URLSearchParams(searchParams);
      if (tab === 'proposal') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t == null || t === '') {
      setActiveTab('proposal');
      setCurrentPage(1);
      return;
    }
    if (VALID_TABS.has(t)) {
      setActiveTab(t);
      setCurrentPage(1);
    } else {
      setActiveTab('proposal');
      setCurrentPage(1);
    }
  }, [searchParams]);
  
  const getTotalPages = () => {
    if (activeTab === 'proposal') return 7;
    if (activeTab === 'contracts') return 6;
    if (activeTab === 'buildProposal') return 1;
    if (activeTab === 'buildContract') return 1;
    return 1;
  };

  const handleNextPage = () => {
    const totalPages = getTotalPages();
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const getPageTitle = (page) => {
    if (activeTab === 'proposal') {
      const proposalTitles = {
        1: "Cover Page",
        2: "Scope of Work",
        3: "Proposed Budget",
        4: "Timeline",
        5: "Next Steps",
        6: "About Us",
        7: "Measure Meeting"
      };
      return proposalTitles[page] || "";
    } else if (activeTab === 'contracts') {
      const contractTitles = {
        1: "Overview",
        2: "Agreement Page 1",
        3: "Agreement Page 2",
        4: "Agreement Page 3",
        5: "Agreement Page 4",
        6: "Signatures"
      };
      return contractTitles[page] || "";
    } else if (activeTab === 'buildProposal') {
      return "Build Proposal";
    } else if (activeTab === 'buildContract') {
      return "Build Contract";
    }
    return "";
  };

  const getPagePDF = (page) => {
    if (activeTab === 'proposal') {
      return `/proposal-example-pg${page}.pdf#toolbar=0&navpanes=0&scrollbar=0`;
    } else if (activeTab === 'contracts') {
      return `/contract-example-pg${page}.pdf#toolbar=0&navpanes=0&scrollbar=0`;
    } else if (activeTab === 'buildProposal') {
      return `/BuildProposalExample.pdf#toolbar=0&navpanes=0&scrollbar=0`;
    } else if (activeTab === 'buildContract') {
      return `/Buildcontractexample.pdf#toolbar=0&navpanes=0&scrollbar=0`;
    }
    return '';
  };

  const renderContent = () => {
    return (
      <div className="space-y-6">
        {/* Page Navigation */}
        {(activeTab === 'proposal' || activeTab === 'contracts') && (
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className={`flex items-center px-4 py-2 rounded-md ${
                currentPage === 1
                  ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
              }`}
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Previous
            </button>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Page {currentPage} of {getTotalPages()}
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === getTotalPages()}
              className={`flex items-center px-4 py-2 rounded-md ${
                currentPage === getTotalPages()
                  ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
              }`}
            >
              Next
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        )}

        {/* Page Content */}
        <div className="rounded-lg p-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 text-center">
              {getPageTitle(currentPage)}
            </h2>
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="w-full h-[800px] border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                  <iframe
                    src={getPagePDF(currentPage)}
                    className="w-full h-full"
                    title={`PDF Viewer - ${getPageTitle(currentPage)}`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={embedded ? '' : 'relative min-h-screen p-6'}>
      {!embedded && <PortalBackground />}
      <div className="relative max-w-7xl mx-auto">
        {!embedded && <SalesPageHeader title="Proposal Template" />}
        {embedded && (
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white text-center mb-6">Proposal Template</h1>
        )}

        {/* Tabs */}
        <div className="rounded-lg mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex justify-center space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => selectTab('proposal')}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center
                  ${activeTab === 'proposal'
                    ? 'border-red-500 text-red-600 dark:text-red-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                `}
              >
                <FileText className="w-5 h-5 mr-2" />
                Design Proposal
              </button>
              <button
                onClick={() => selectTab('contracts')}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center
                  ${activeTab === 'contracts'
                    ? 'border-red-500 text-red-600 dark:text-red-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                `}
              >
                <FileCheck className="w-5 h-5 mr-2" />
                Design Contract
              </button>
              <button
                onClick={() => selectTab('buildProposal')}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center
                  ${activeTab === 'buildProposal'
                    ? 'border-red-500 text-red-600 dark:text-red-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                `}
              >
                <FileText className="w-5 h-5 mr-2" />
                Build Proposal
              </button>
              <button
                onClick={() => selectTab('buildContract')}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center
                  ${activeTab === 'buildContract'
                    ? 'border-red-500 text-red-600 dark:text-red-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                `}
              >
                <FileCheck className="w-5 h-5 mr-2" />
                Build Contract
              </button>
            </nav>
          </div>
          {/* Download Links */}
          {activeTab !== 'buildProposal' && (
            <div className="flex justify-center py-3">
              <a
                href={activeTab === 'proposal' 
                  ? "https://www.canva.com/design/DAGuwkJ349Y/ZX-C78dfocQj0ztQfAisyQ/view?utm_content=DAGuwkJ349Y&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview"
                  : activeTab === 'contracts'
                  ? "/Design-Contract-Template.docx"
                  : activeTab === 'buildContract'
                  ? "/ConstructionContractTemplate.docx"
                  : "#"
                }
                target={activeTab === 'proposal' ? "_blank" : undefined}
                rel={activeTab === 'proposal' ? "noopener noreferrer" : undefined}
                download={activeTab === 'contracts' ? "Design-Contract-Template.docx" : activeTab === 'buildContract' ? "ConstructionContractTemplate.docx" : undefined}
                className="inline-flex items-center text-sm text-white bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 rounded-full px-4 py-2 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download the template
              </a>
            </div>
          )}
        </div>
        
        {/* Content */}
        {renderContent()}
      </div>
    </div>
  );
};

export default PortalSalesProposals; 